import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { flushSync } from "react-dom";
import "./darkTheme.css";

const ThemeContext = createContext(null);

const MOBILE_BREAKPOINT = 768;
const STORAGE_KEY = "theme";
const SWITCHING_CLASS = "theme-switching";

const readStoredTheme = () => {
  try {
    return localStorage.getItem(STORAGE_KEY);
  } catch {
    return null;
  }
};

const getInitialIsDark = () => {
  const stored = readStoredTheme();
  if (stored) return stored === "dark";
  return window.matchMedia?.("(prefers-color-scheme: dark)")?.matches ?? false;
};

const applyThemeToDocument = (isDark) => {
  const root = document.documentElement;
  const mode = isDark ? "dark" : "light";
  root.setAttribute("data-theme", mode);
  // Bootstrap 5.3 colour modes: themes cards, forms, dropdowns, modals, tables.
  root.setAttribute("data-bs-theme", mode);
  root.style.colorScheme = mode;
};

// Apply before first paint so there is no light flash on a dark reload.
applyThemeToDocument(getInitialIsDark());

export const ThemeProvider = ({ children }) => {
  const [isMobile, setIsMobile] = useState(() => window.innerWidth < MOBILE_BREAKPOINT);
  const [isDark, setIsDarkState] = useState(getInitialIsDark);
  const isDarkRef = useRef(isDark);
  isDarkRef.current = isDark;

  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth < MOBILE_BREAKPOINT);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  useEffect(() => {
    applyThemeToDocument(isDark);
    try {
      localStorage.setItem(STORAGE_KEY, isDark ? "dark" : "light");
    } catch {
      // Storage can be unavailable (private mode); the theme still applies.
    }
  }, [isDark]);

  // Swaps theme with a single GPU-composited crossfade (View Transitions API)
  // instead of animating every element's colours, which forces thousands of
  // style recalcs/repaints per frame. Falls back to an instant swap.
  const setIsDark = useCallback((next) => {
    const root = document.documentElement;
    const value = !!(typeof next === "function" ? next(isDarkRef.current) : next);
    const commit = () => {
      applyThemeToDocument(value);
      setIsDarkState(value);
    };

    // Kill per-element CSS transitions while swapping; otherwise they animate
    // underneath the crossfade and cause visible flicker.
    root.classList.add(SWITCHING_CLASS);
    const release = () => root.classList.remove(SWITCHING_CLASS);

    const reduceMotion = window.matchMedia?.("(prefers-reduced-motion: reduce)")?.matches;
    if (typeof document.startViewTransition === "function" && !reduceMotion) {
      const transition = document.startViewTransition(() => {
        flushSync(commit);
      });
      transition.finished.then(release, release);
      return;
    }

    commit();
    window.requestAnimationFrame(() => window.requestAnimationFrame(release));
  }, []);

  const toggleTheme = useCallback(() => setIsDark((prev) => !prev), [setIsDark]);

  const value = useMemo(
    () => ({ isDark, setIsDark, toggleTheme, isMobile }),
    [isDark, setIsDark, toggleTheme, isMobile]
  );

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
};

export const useTheme = () => {
  const ctx = useContext(ThemeContext);
  if (!ctx) throw new Error("useTheme must be used inside ThemeProvider");
  return ctx;
};

// An explicit boolean prop wins; otherwise follow the app-wide theme.
export const useResolvedDark = (isDarkProp) => {
  const { isDark } = useTheme();
  return typeof isDarkProp === "boolean" ? isDarkProp : isDark;
};
